import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Users, Check, Star, Info } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function RsvpBlock({ event, onChange }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [me, setMe] = useState({ status: null, guests: 0 });
  const [counts, setCounts] = useState({ going: event.going_count || 0, interested: event.interested_count || 0 });
  const [busy, setBusy] = useState(false);
  const closed = !!event.rsvp_closed_at || new Date(`${(event.date || "").slice(0, 10)}T${event.time || "00:00"}`) <= new Date();
  const full = event.rsvp_capacity != null && counts.going >= event.rsvp_capacity && me.status !== "going";
  useEffect(() => { if (user && event.rsvp_enabled) api.get(`/events/${event.id}/rsvp/me`).then(r => setMe(r.data)).catch(() => {}); }, [user, event.id, event.rsvp_enabled]);
  if (!event.rsvp_enabled) return null;

  const set = async (status, guests = me.guests) => {
    if (!user) { toast.info("Inicia sessão para confirmar presença"); nav(`/login?next=/eventos/${event.id}`); return; }
    setBusy(true);
    try {
      const r = await api.post(`/events/${event.id}/rsvp`, { status, guests });
      setMe({ status: r.data.status, guests: r.data.guests }); setCounts({ going: r.data.going_count, interested: r.data.interested_count }); onChange?.(r.data);
      toast.success(status === "going" ? "Presença confirmada" : status === "interested" ? "Marcado como interessado" : "Presença retirada");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erro"); }
    finally { setBusy(false); }
  };
  const Btn = ({ st, icon: I, label, testid }) => (
    <button data-testid={testid} disabled={busy || closed || (st === "going" && full)} onClick={() => set(me.status === st ? "cancelled" : st)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-semibold border transition-colors disabled:opacity-50 ${me.status === st ? "bg-black text-white border-black" : "border-zinc-300 hover:border-black"}`}>
      <I size={13} fill={me.status === st ? "currentColor" : "none"} /> {label}
    </button>
  );
  return (
    <div data-testid="rsvp-block" className="mb-4 p-4 rounded-2xl bg-zinc-50 border border-zinc-200">
      <div className="flex items-center justify-between mb-3 text-xs">
        <span className="font-semibold flex items-center gap-1.5"><Users size={13} /> Confirmação de presença</span>
        <span data-testid="rsvp-counts" className="text-zinc-600">{counts.going} vão · {counts.interested} interessados{event.rsvp_capacity != null && <span className="text-zinc-400"> · {event.rsvp_capacity} lugares</span>}</span>
      </div>
      {closed ? <p data-testid="rsvp-closed" className="text-xs text-zinc-500 italic">As confirmações estão fechadas.</p> : (
        <div className="flex gap-2">
          <Btn st="going" icon={Check} label={full ? "Lotado" : me.status === "going" ? "Vou ✓" : "Vou"} testid="rsvp-going" />
          <Btn st="interested" icon={Star} label={me.status === "interested" ? "Interessado ✓" : "Interessado"} testid="rsvp-interested" />
        </div>
      )}
      {full && !closed && me.status !== "going" && <p data-testid="rsvp-full-hint" className="text-[11px] text-zinc-500 mt-2">Evento lotado — podes marcar como Interessado.</p>}
      {me.status === "going" && !closed && (
        <label className="flex items-center gap-2 text-xs mt-3">Convidados:
          <select data-testid="rsvp-guests" value={me.guests} disabled={busy} onChange={e => set("going", Number(e.target.value))} className="border border-zinc-300 rounded-lg px-2 py-1 bg-white">{[0, 1, 2, 3, 4].map(n => <option key={n} value={n}>{n}</option>)}</select>
        </label>
      )}
      <p className="text-[10px] text-zinc-500 mt-3 flex items-start gap-1"><Info size={11} className="shrink-0 mt-px" /> Confirmar presença não é bilhete nem garante entrada.{event.ticket_link ? " Os bilhetes são adquiridos no link do organizador." : ""}</p>
    </div>
  );
}
